import React, { useRef } from 'react';
import { Box, Button, Grid, Tooltip, Typography } from '@mui/material';
import DirectionsWalkRoundedIcon from '@mui/icons-material/DirectionsWalkRounded';
import DonutLargeRoundedIcon from '@mui/icons-material/DonutLargeRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import PictureAsPdfRoundedIcon from '@mui/icons-material/PictureAsPdfRounded';
import DescriptionRoundedIcon from '@mui/icons-material/DescriptionRounded';
import html2pdf from 'html2pdf.js';
import {
  AlignmentType,
  BorderStyle,
  Document,
  HeadingLevel,
  ImageRun,
  Packer,
  PageOrientation,
  Paragraph,
  ShadingType,
  Table,
  TableCell,
  TableRow,
  TextRun,
  WidthType,
} from 'docx';
import { saveAs } from 'file-saver';

const REPORT_WIDTH = 1360;
const colors = {
  blue: '#4353f4',
  navy: '#17218f',
  grid: '#e3e7ef',
  text: '#111827',
  muted: '#637083',
};

const summaryData = [
  {
    label: 'Total Visitors',
    value: '45,689',
    detail: '↗ 12.5%',
    suffix: 'vs Previous Day',
    color: '#4353f4',
    tint: '#eef2ff',
    icon: DirectionsWalkRoundedIcon,
  },
  {
    label: 'Average Dwell Time',
    value: '04:32',
    detail: '↗ 8.3%',
    suffix: 'vs Previous Day',
    color: '#00a65a',
    tint: '#e9f8f0',
    icon: DonutLargeRoundedIcon,
  },
  {
    label: 'Peak Time',
    value: '3:00 PM - 4:00 PM',
    detail: 'Sunday, 04 May 2026',
    color: '#f78b22',
    tint: '#fff4e9',
    icon: GroupsRoundedIcon,
  },
];

const zoneData = [
  { zone: 'Entrance', visitors: '9,842', average: '00:48', max: '03:12', color: '#4353f4', tint: '#f0f4ff' },
  { zone: 'Aisle 1', visitors: '14,256', average: '02:15', max: '08:45', color: '#29a334', tint: '#eff9e8' },
  { zone: 'Aisle 2', visitors: '12,356', average: '01:52', max: '07:30', color: '#f58220', tint: '#fff5e8' },
  { zone: 'Checkout', visitors: '8,654', average: '03:05', max: '09:10', color: '#e51b32', tint: '#fff0f0' },
  { zone: 'Garden Center', visitors: '3,125', average: '03:10', max: '10:25', color: '#e51c91', tint: '#f8edff' },
];

const hourlyData = [
  ['07:00 AM - 08:00 AM', '2,145', '4.69%', 35],
  ['08:00 AM - 09:00 AM', '3,256', '7.12%', 52],
  ['09:00 AM - 10:00 AM', '4,875', '10.66%', 72],
  ['10:00 AM - 11:00 AM', '5,842', '12.78%', 84],
  ['11:00 AM - 12:00 PM', '6,542', '14.32%', 94],
  ['12:00 PM - 01:00 PM', '6,125', '13.40%', 85],
  ['01:00 PM - 02:00 PM', '5,965', '13.06%', 83],
  ['02:00 PM - 03:00 PM', '6,845', '14.97%', 98],
  ['03:00 PM - 04:00 PM', '5,689', '12.44%', 80],
  ['04:00 PM - 05:00 PM', '3,256', '7.12%', 45],
  ['05:00 PM - 06:00 PM', '2,149', '4.71%', 27],
];

const trendData = [
  ['01 May', 13000],
  ['02 May', 14100],
  ['03 May', 14600],
  ['04 May', 20400],
  ['05 May', 14200],
  ['06 May', 14100],
  ['07 May', 13000],
];

const sectionTitleSx = {
  color: colors.navy,
  fontSize: 15,
  fontWeight: 800,
  letterSpacing: 0.1,
  mb: 1.4,
};

const panelSx = {
  bgcolor: '#fff',
  border: `1px solid ${colors.grid}`,
  borderRadius: '5px',
  p: 1.5,
  boxSizing: 'border-box',
};

function SummaryCard({ item }) {
  const Icon = item.icon;
  return (
    <Box sx={{ ...panelSx, height: 118, display: 'flex', alignItems: 'center', px: 3 }}>
      <Box
        sx={{
          width: 68,
          height: 68,
          borderRadius: '50%',
          bgcolor: item.tint,
          display: 'grid',
          placeItems: 'center',
          mr: 2.5,
          flexShrink: 0,
        }}
      >
        <Icon sx={{ color: item.color, fontSize: 39 }} />
      </Box>
      <Box>
        <Typography sx={{ fontSize: 15, color: colors.text, mb: 0.25 }}>{item.label}</Typography>
        <Typography sx={{ fontSize: 24, lineHeight: 1.25, fontWeight: 700, color: '#070b14' }}>{item.value}</Typography>
        <Typography sx={{ fontSize: 13, mt: 0.8, color: colors.text }}>
          <Box component="span" sx={{ color: item.label === 'Peak Time' ? colors.text : '#00a550', fontWeight: 700 }}>
            {item.detail}
          </Box>{' '}
          {item.suffix}
        </Typography>
      </Box>
    </Box>
  );
}

const tableSx = {
  width: '100%',
  borderCollapse: 'collapse',
  tableLayout: 'fixed',
  '& th, & td': {
    border: `1px solid ${colors.grid}`,
    height: 35,
    px: 1.4,
    py: 0,
    fontSize: 12.5,
    color: colors.text,
    fontWeight: 500,
    boxSizing: 'border-box',
  },
  '& th': { height: 38, fontWeight: 600, bgcolor: '#fff' },
};

function ZoneSummary() {
  return (
    <Box sx={{ ...panelSx, height: 280 }}>
      <Typography sx={sectionTitleSx}>ZONE WISE SUMMARY</Typography>
      <Box component="table" sx={tableSx}>
        <thead>
          <tr>
            <th style={{ textAlign: 'left' }}>Zone</th>
            <th>Total Visitors</th>
            <th>Avg Dwell Time</th>
            <th>Max Dwell Time</th>
          </tr>
        </thead>
        <tbody>
          {zoneData.map((item) => (
            <tr key={item.zone}>
              <td>
                <Box sx={{ display: 'flex', alignItems: 'center', color: item.color }}>
                  <Box sx={{ width: 11, height: 11, borderRadius: '50%', bgcolor: item.color, mr: 1.25 }} />
                  {item.zone}
                </Box>
              </td>
              <td style={{ textAlign: 'center' }}>{item.visitors}</td>
              <td style={{ textAlign: 'center' }}>{item.average}</td>
              <td style={{ textAlign: 'center' }}>{item.max}</td>
            </tr>
          ))}
        </tbody>
      </Box>
    </Box>
  );
}

function HourlySummary() {
  return (
    <Box sx={{ ...panelSx, height: 438 }}>
      <Typography sx={sectionTitleSx}>HOURLY FOOTFALL SUMMARY</Typography>
      <Box component="table" sx={{ ...tableSx, '& th, & td': { ...tableSx['& th, & td'], height: 31 } }}>
        <thead>
          <tr>
            <th style={{ width: '28%', textAlign: 'left' }}>Hour</th>
            <th style={{ width: '24%' }}>Total Visitors</th>
            <th style={{ width: '20%' }}>% of Total</th>
            <th style={{ width: '28%' }} aria-label="Percentage visualization" />
          </tr>
        </thead>
        <tbody>
          {hourlyData.map(([hour, visitors, percentage, width]) => (
            <tr key={hour}>
              <td>{hour}</td>
              <td style={{ textAlign: 'center', fontWeight: 600 }}>{visitors}</td>
              <td style={{ textAlign: 'center', fontWeight: 600 }}>{percentage}</td>
              <td>
                <Box sx={{ width: `${width}%`, height: 11, bgcolor: colors.blue, borderRadius: '2px' }} />
              </td>
            </tr>
          ))}
        </tbody>
      </Box>
    </Box>
  );
}

function DwellAnalysis() {
  return (
    <Box sx={{ ...panelSx, height: 146 }}>
      <Typography sx={sectionTitleSx}>
        DWELL TIME ANALYSIS{' '}
        <Box component="span" sx={{ color: '#526078', fontSize: 12.5, fontWeight: 500 }}>
          (Average Dwell Time in mm:ss)
        </Box>
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 1 }}>
        {zoneData.map((item) => (
          <Box key={item.zone} sx={{ height: 81, borderRadius: '4px', bgcolor: item.tint, display: 'grid', placeItems: 'center' }}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography sx={{ fontSize: 12, color: item.color, mb: 0.7 }}>{item.zone}</Typography>
              <Typography sx={{ fontSize: 19, fontWeight: 700, color: '#111' }}>{item.average}</Typography>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

function DailyTrend() {
  const max = 22000;
  return (
    <Box sx={{ ...panelSx, height: 213, mt: 1.2, pb: 0.8 }}>
      <Typography sx={{ ...sectionTitleSx, mb: 0.5 }}>DAILY FOOTFALL TREND</Typography>
      <Box sx={{ height: 122, display: 'flex' }}>
        <Box sx={{ width: 38, mr: 0.8, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', pb: '20px' }}>
          {['20K', '10K', '0'].map((label) => (
            <Typography key={label} sx={{ fontSize: 11, textAlign: 'right', color: '#273142' }}>
              {label}
            </Typography>
          ))}
        </Box>
        <Box sx={{ flex: 1 }}>
          <Box
            sx={{
              height: 102,
              borderLeft: '1px solid #d8dde7',
              borderBottom: '1px solid #d8dde7',
              backgroundImage: 'linear-gradient(to bottom, #e7eaf0 1px, transparent 1px)',
              backgroundSize: '100% 51px',
              display: 'flex',
              justifyContent: 'space-around',
              alignItems: 'flex-end',
            }}
          >
            {trendData.map(([day, value]) => (
              <Box key={day} sx={{ width: '12%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'flex-end' }}>
                <Box sx={{ width: 47, height: `${(value / max) * 100}%`, bgcolor: colors.blue, borderRadius: '3px 3px 0 0' }} />
              </Box>
            ))}
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-around' }}>
            {trendData.map(([day]) => (
              <Typography key={day} sx={{ width: '12%', fontSize: 11.5, pt: 0.6, textAlign: 'center', whiteSpace: 'nowrap' }}>
                {day}
              </Typography>
            ))}
          </Box>
        </Box>
      </Box>
      {/* <Typography sx={{ fontSize: 11.5, color: colors.muted, mt: 1.2 }}>Note: All values are approximate and for reference only.</Typography> */}
    </Box>
  );
}

function ReportCanvas({ reportRef }) {
  return (
    <Box
      ref={reportRef}
      sx={{ width: REPORT_WIDTH, minWidth: REPORT_WIDTH, bgcolor: '#fff', p: 1.2, boxSizing: 'border-box', fontFamily: 'Arial, sans-serif' }}
    >
      <Box sx={{ display: 'grid', gridTemplateColumns: '380px 360px 1fr', gap: 1.4 }}>
        {summaryData.map((item) => <SummaryCard key={item.label} item={item} />)}
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.4, mt: 1.4 }}>
        <Box sx={{ display: 'grid', gap: 1.2 }}>
          <ZoneSummary />
          <DwellAnalysis />
        </Box>
        <HourlySummary />
      </Box>
      <DailyTrend />
    </Box>
  );
}

const docBorders = {
  top: { style: BorderStyle.SINGLE, size: 1, color: 'DDE2EA' },
  bottom: { style: BorderStyle.SINGLE, size: 1, color: 'DDE2EA' },
  left: { style: BorderStyle.SINGLE, size: 1, color: 'DDE2EA' },
  right: { style: BorderStyle.SINGLE, size: 1, color: 'DDE2EA' },
  insideHorizontal: { style: BorderStyle.SINGLE, size: 1, color: 'DDE2EA' },
  insideVertical: { style: BorderStyle.SINGLE, size: 1, color: 'DDE2EA' },
};

function wordCell(text, options = {}) {
  return new TableCell({
    shading: options.fill ? { type: ShadingType.CLEAR, fill: options.fill } : undefined,
    children: [
      new Paragraph({
        alignment: options.align || AlignmentType.CENTER,
        spacing: { before: 55, after: 55 },
        children: [new TextRun({ text: String(text), bold: options.bold, color: options.color || '111827', size: options.size || 18 })],
      }),
    ],
  });
}

function wordHeading(text) {
  return new Paragraph({
    heading: HeadingLevel.HEADING_2,
    spacing: { before: 180, after: 90 },
    children: [new TextRun({ text, bold: true, color: '17218F', size: 22 })],
  });
}

function buildChartImage() {
  return new Promise((resolve) => {
    const canvas = document.createElement('canvas');
    canvas.width = 1050;
    canvas.height = 250;
    const context = canvas.getContext('2d');
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.strokeStyle = '#dde2ea';
    context.lineWidth = 1;
    [25, 105, 185].forEach((y) => {
      context.beginPath();
      context.moveTo(55, y);
      context.lineTo(1030, y);
      context.stroke();
    });
    context.fillStyle = '#344054';
    context.font = '14px Arial';
    context.fillText('20K', 10, 31);
    context.fillText('10K', 10, 111);
    context.fillText('0', 30, 191);
    trendData.forEach(([day, value], index) => {
      const x = 95 + index * 137;
      const height = (value / 22000) * 160;
      context.fillStyle = colors.blue;
      context.fillRect(x, 185 - height, 50, height);
      context.fillStyle = '#111827';
      context.fillText(day, x, 218);
    });
    canvas.toBlob(async (blob) => resolve(new Uint8Array(await blob.arrayBuffer())), 'image/png');
  });
}

export default function InteriorReport({ fileName }) {
  const reportRef = useRef(null);
  const exportBaseName = (fileName || 'Interior_Visitor_Report').replace(/\.(pdf|docx)$/i, '');

  const downloadPDF = async () => {
    if (!reportRef.current) return;
    const clone = reportRef.current.cloneNode(true);
    const exportContainer = document.createElement('div');
    exportContainer.style.position = 'fixed';
    exportContainer.style.left = '-10000px';
    exportContainer.style.top = '0';
    exportContainer.style.width = `${REPORT_WIDTH}px`;
    exportContainer.style.backgroundColor = '#ffffff';
    clone.style.margin = '0';
    exportContainer.appendChild(clone);
    document.body.appendChild(exportContainer);

    try {
      await html2pdf()
        .from(clone)
        .set({
          margin: 0,
          filename: `${exportBaseName}.pdf`,
          image: { type: 'jpeg', quality: 0.98 },
          html2canvas: { scale: 2, useCORS: true, backgroundColor: '#ffffff', windowWidth: REPORT_WIDTH },
          jsPDF: { unit: 'px', format: [REPORT_WIDTH, 820], orientation: 'landscape', hotfixes: ['px_scaling'] },
          pagebreak: { mode: ['avoid-all'] },
        })
        .save();
    } finally {
      document.body.removeChild(exportContainer);
    }
  };

  const downloadWord = async () => {
    const chartImage = await buildChartImage();
    const summaryTable = new Table({
      width: { size: 100, type: WidthType.PERCENTAGE },
      borders: docBorders,
      rows: [
        new TableRow({
          children: summaryData.map((item) => new TableCell({
            shading: { type: ShadingType.CLEAR, fill: item.tint.slice(1) },
            children: [
              new Paragraph({ alignment: AlignmentType.CENTER, spacing: { before: 100, after: 50 }, children: [new TextRun({ text: item.label, size: 21 })] }),
              new Paragraph({ alignment: AlignmentType.CENTER, spacing: { after: 45 }, children: [new TextRun({ text: item.value, bold: true, size: 31 })] }),
              new Paragraph({ alignment: AlignmentType.CENTER, spacing: { after: 100 }, children: [new TextRun({ text: `${item.detail} ${item.suffix || ''}`.trim(), color: item.color.slice(1), size: 18 })] }),
            ],
          })),
        }),
      ],
    });

    const zoneTable = new Table({
      width: { size: 100, type: WidthType.PERCENTAGE },
      borders: docBorders,
      rows: [
        new TableRow({ children: ['Zone', 'Total Visitors', 'Avg Dwell Time', 'Max Dwell Time'].map((text) => wordCell(text, { bold: true, fill: 'F5F7FA' })) }),
        ...zoneData.map((item) => new TableRow({ children: [wordCell(`●  ${item.zone}`, { align: AlignmentType.LEFT, color: item.color.slice(1) }), wordCell(item.visitors), wordCell(item.average), wordCell(item.max)] })),
      ],
    });

    const hourlyTable = new Table({
      width: { size: 100, type: WidthType.PERCENTAGE },
      borders: docBorders,
      rows: [
        new TableRow({ children: ['Hour', 'Total Visitors', '% of Total', 'Percentage'].map((text) => wordCell(text, { bold: true, fill: 'F5F7FA' })) }),
        ...hourlyData.map(([hour, visitors, percentage, width]) => new TableRow({ children: [wordCell(hour, { align: AlignmentType.LEFT }), wordCell(visitors), wordCell(percentage), wordCell(`${'█'.repeat(Math.max(1, Math.round(width / 10)))}`, { align: AlignmentType.LEFT, color: '4353F4' })] })),
      ],
    });

    const dwellTable = new Table({
      width: { size: 100, type: WidthType.PERCENTAGE },
      borders: docBorders,
      rows: [
        new TableRow({ children: zoneData.map((item) => new TableCell({
          shading: { type: ShadingType.CLEAR, fill: item.tint.slice(1) },
          children: [
            new Paragraph({ alignment: AlignmentType.CENTER, spacing: { before: 70, after: 40 }, children: [new TextRun({ text: item.zone, color: item.color.slice(1), size: 18 })] }),
            new Paragraph({ alignment: AlignmentType.CENTER, spacing: { after: 70 }, children: [new TextRun({ text: item.average, bold: true, size: 25 })] }),
          ],
        })) }),
      ],
    });

    const wordDocument = new Document({
      sections: [{
        properties: {
          page: {
            size: { orientation: PageOrientation.LANDSCAPE },
            margin: { top: 450, right: 500, bottom: 450, left: 500 },
          },
        },
        children: [
          new Paragraph({ alignment: AlignmentType.CENTER, spacing: { after: 180 }, children: [new TextRun({ text: 'INTERIOR VISITOR REPORT', bold: true, color: '17218F', size: 30 })] }),
          summaryTable,
          wordHeading('ZONE WISE SUMMARY'),
          zoneTable,
          wordHeading('HOURLY FOOTFALL SUMMARY'),
          hourlyTable,
          wordHeading('DWELL TIME ANALYSIS (Average Dwell Time in mm:ss)'),
          dwellTable,
          wordHeading('DAILY FOOTFALL TREND'),
          new Paragraph({ alignment: AlignmentType.CENTER, children: [new ImageRun({ data: chartImage, transformation: { width: 700, height: 167 }, type: 'png' })] }),
          // new Paragraph({ spacing: { before: 90 }, children: [new TextRun({ text: 'Note: All values are approximate and for reference only.', color: '637083', italics: true, size: 17 })] }),
        ],
      }],
    });

    const blob = await Packer.toBlob(wordDocument);
    saveAs(blob, `${exportBaseName}.docx`);
  };

  return (
    <>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} mt={2} sx={{ display: 'flex', justifyContent: 'flex-end', pr: 2 }}>
          <Tooltip title="Download PDF" arrow>
            <Button
              id="daily-escalation-download-trigger"
              aria-label="Download PDF"
              variant="contained"
              onClick={downloadPDF}
              sx={{ minWidth: 34, width: 34, height: 32, p: 0, bgcolor: '#d32f2f', '&:hover': { bgcolor: '#b71c1c' } }}
            >
              <PictureAsPdfRoundedIcon sx={{ fontSize: 19 }} />
            </Button>
          </Tooltip>
          <Tooltip title="Download Word" arrow>
            <Button
              id="daily-escalation-download-trigger-word"
              aria-label="Download Word"
              variant="contained"
              sx={{ ml: 1.25, minWidth: 34, width: 34, height: 32, p: 0, bgcolor: '#1565c0', '&:hover': { bgcolor: '#0d47a1' } }}
              onClick={downloadWord}
            >
              <DescriptionRoundedIcon sx={{ fontSize: 19 }} />
            </Button>
          </Tooltip>
        </Grid>
      </Grid>

      <Box sx={{ display: 'flex', justifyContent: 'center', bgcolor: '#f5f5f5', p: 2, overflowX: 'auto' }}>
        <ReportCanvas reportRef={reportRef} />
      </Box>
    </>
  );
}
